
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Car } from '@/contexts/CarContext';
import { Car as CarIcon, BookmarkCheck, MoreHorizontal, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from '@/components/ui/dropdown-menu';
import { Badge } from '@/components/ui/badge';
import StatusWidget from './StatusWidget';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';
import { getSignedViewUrl, type SignedUrlInfo } from '@/lib/getSignedViewUrl';
import { useI18n } from '@/i18n/LanguageContext';

interface CarCardProps {
  car: Car;
  selected?: boolean;
  isMainCar?: boolean;
  onClick?: () => void;
  onRequestSetMain?: () => void;
  onRequestOpenDetails?: () => void;
}

const CarCard: React.FC<CarCardProps> = ({ car, selected, isMainCar, onClick, onRequestSetMain, onRequestOpenDetails }) => {
  const navigate = useNavigate();
  const { t } = useI18n();
  const [signedUrlInfo, setSignedUrlInfo] = useState<SignedUrlInfo | null>(null);
  const [imageLoading, setImageLoading] = useState<boolean>(false);

  useEffect(() => {
    let isMounted = true;
    const load = async () => {
      if (!car.image_path) {
        setSignedUrlInfo(null);
        return;
      }
      try {
        setImageLoading(true);
        const info = await getSignedViewUrl(car.image_path);
        if (isMounted) setSignedUrlInfo(info);
      } catch (e) {
        if (isMounted) setSignedUrlInfo(null);
      } finally {
        if (isMounted) setImageLoading(false);
      }
    };
    load();
    return () => {
      isMounted = false;
    };
  }, [car.image_path]);

  return (
    <div
      className={cn(
        "relative w-full rounded-[20px] p-3 cursor-pointer",
      )}
      style={{ boxShadow: '0px 10px 8px -1px #EEEEEE', background: '#FDFDFD', border: '1px solid #e4e4e7' }}
      onClick={onClick}
    >
      <div className="flex justify-between items-start">
        {/* Column 1 (left): Car image with fixed 2:1 ratio */}
        <div className="basis-1/2 max-w-[50%]">
          <div className="relative w-full" style={{ aspectRatio: '1.7 / 1' }}>
            {/* Main badge in image (top-left) */}
            {isMainCar && (
              <div className="absolute top-0 left-0 z-30 m-0.5">
                <Badge variant="default" className="text-xs leading-none px-1.5 py-0.5">{t('cars.main')}</Badge>
              </div>
            )}
            {/* Kebab menu overlayed on image top-right (styled like HomePage carousel) */}
            <div className="absolute top-0 right-0 z-30 m-0.5">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    className="p-1 bg-white rounded-full shadow hover:bg-gray-100"
                    onClick={(e) => e.stopPropagation()}
                    aria-label={t('home.actions')}
                    type="button"
                  >
                    <MoreHorizontal size={18} className="text-gray-600" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-44" onClick={(e) => e.stopPropagation()}>
                  <DropdownMenuItem disabled={isMainCar} onClick={() => onRequestSetMain && onRequestSetMain()}>
                    {isMainCar ? (
                      <span className="inline-flex items-center gap-2 opacity-60"><Check className="h-4 w-4" /> {t('cars.setAsMainCar')}</span>
                    ) : (
                      <span className="inline-flex items-center gap-2">{t('cars.setAsMainCar')}</span>
                    )}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => onRequestOpenDetails ? onRequestOpenDetails() : onClick && onClick()}>
                    {t('cars.openDetails')}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
            {signedUrlInfo?.url ? (
              <img
                src={signedUrlInfo.url}
                alt={car.nickname || `${car.brand} ${car.model}`}
                className="absolute inset-0 w-full h-full object-cover rounded"
                referrerPolicy="no-referrer"
                crossOrigin="anonymous"
                onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
              />
            ) : (
              <div className="absolute inset-0 flex items-center justify-center">
                <CarIcon className="text-gray-400" size={32} />
              </div>
            )}
          </div>
        </div>
        {/* Column 2 (right): Details aligned to image top */}
        <div className="text-right basis-1/2 max-w-[50%] flex flex-col justify-start">
          <h3 className="text-base font-semibold">{car.nickname || `${car.brand} ${car.model}`}</h3>
          <p className="text-sm text-gray-500">{car.brand} {car.model}</p>
          <p className="text-sm text-gray-500">{car.licensePlate || car.year}</p>
          {/* Mileage hidden as requested */}
        </div>
      </div>
      <div className="mt-1 flex items-center justify-between">
        <StatusWidget carId={car.id} issues={car.issues || []} compact />
        <Button
          variant="destructive"
          size="sm"
          className="h-9 px-6 py-2 text-sm inline-flex items-center gap-2"
          onClick={(e) => { e.stopPropagation(); navigate(`/reports/health/${car.id}`); }}
        >
          {t('home.healthReport')}
        </Button>
      </div>
    </div>
  );
};

export default CarCard;
